import dayjs, { Dayjs } from 'dayjs';
import type { InvoiceNotification } from '../types/invoiceNotification';
import type { PurchaseFilterPeriod } from '../types/purchase';

type PurchaseAmountInput = {
  quantity?: number;
  unitPrice?: number;
  total?: number;
  isTransferOut?: boolean;
};

const normalizeStatus = (value?: string): string =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export const parseInventoryDate = (value?: string): Dayjs | null => {
  const raw = String(value || '').trim();
  if (!raw) return null;

  const vietnameseDate = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const isoDate = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (vietnameseDate || isoDate) {
    const [, first, second, third] = (vietnameseDate || isoDate)!;
    const dayText = vietnameseDate ? first : third;
    const monthText = second;
    const yearText = vietnameseDate ? third : first;
    const day = Number(dayText);
    const month = Number(monthText);
    const year = Number(yearText);
    const parsed = dayjs(new Date(year, month - 1, day));
    if (
      parsed.year() !== year
      || parsed.month() !== month - 1
      || parsed.date() !== day
    ) {
      return null;
    }
    return parsed;
  }

  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed : null;
};

export const isWithinInventoryBalanceCutoff = (
  itemDate: Dayjs,
  period: PurchaseFilterPeriod,
  selectedDate: Dayjs,
): boolean => {
  if (!itemDate.isValid() || !selectedDate.isValid()) return false;
  if (period === 'all') return true;

  const cutoff = selectedDate.endOf(period);
  return itemDate.valueOf() <= cutoff.valueOf();
};

export const getPurchaseAmountDelta = (item: PurchaseAmountInput): number => {
  const total = Number(item.total || 0);
  if (!item.isTransferOut) return total;

  const quantity = Math.abs(Number(item.quantity || 0));
  const unitPrice = Number(item.unitPrice || 0);
  return -(quantity * unitPrice);
};

export const isInventoryInvoiceCountable = (
  invoice: Pick<InvoiceNotification, 'quantity' | 'status' | 'isActive'>,
): boolean => {
  if (invoice.isActive === false || Number(invoice.quantity || 0) <= 0) return false;

  const status = normalizeStatus(invoice.status);
  const rejectedStatuses = ['cancel', 'huy', 'refund', 'hoan', 'void'];
  return !rejectedStatuses.some((rejected) => status.includes(rejected));
};
